import React, { useState, useEffect } from "react";
import { MapContainer, TileLayer, Marker, Popup, Polygon, useMap, GeoJSON } from "react-leaflet";
import L from "leaflet";
import axios from "axios";
import {
    Container,
    Grid,
    Button,
    Card,
    CardContent,
    Tab,
    Tabs,
    TextField,
    Typography,
    AccordionSummary,
    AccordionDetails,
    Accordion,
    CircularProgress,
    Box,
    Divider,
    Chip,
    RadioGroup,
    Radio,
    FormControlLabel,
} from "@mui/material";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faArrowRight, faChevronDown, faSearch} from "@fortawesome/free-solid-svg-icons";
import SectionTitle from "@others/title-section.layout";
import {getStreetByName, Street} from "@services/street.service";

// Définition du type des coordonnées
type LatLng = [number, number];

// Centre par défaut (Bois-le-Roi)
const center: LatLng = [48.476, 2.692];

// Icône personnalisée pour les marqueurs
const customIcon = new L.Icon({
    iconUrl: "https://cdn-icons-png.flaticon.com/512/684/684908.png",
    iconSize: [30, 30],
});

// Définition d'un point d'intérêt
interface Point {
    id: number;
    name: string;
    coords: LatLng;
}

const points: Point[] = [
    { id: 1, name: "Gare de Bois-le-Roi", coords: [48.476, 2.692] },
    { id: 2, name: "Point d'intérêt", coords: [48.470, 2.708] },
];

// Zones de catégorie 1 (bleues)
const category1Zones: LatLng[][] = [
    [
        [48.475, 2.704],
        [48.474, 2.709],
        [48.470, 2.710],
        [48.468, 2.705],
    ],
    [
        [48.473, 2.703],
        [48.472, 2.706],
        [48.470, 2.707],
        [48.469, 2.704],
    ],
];

// Zone de catégorie 2 (rouge)
const category2Zone: LatLng[] = [
    [48.471, 2.700],
    [48.470, 2.704],
    [48.468, 2.705],
    [48.467, 2.701],
];

// Typage des résultats de la recherche sur Nominatim
interface NominatimResult {
    lat: string;
    lon: string;
    name: string;
    address: {
        road?: string;
        village?: string;
        postcode?: string;
        country?: string;
    };
}

// Composant qui permet de centrer la carte sur la position sélectionnée
interface FlyToLocationProps {
    position: LatLng | null;
}

const FlyToLocation: React.FC<FlyToLocationProps> = ({ position }) => {
    const map = useMap();
    useEffect(() => {
        if (position) {
            map.flyTo(position, 17, { duration: 1.5 });
        }
    }, [position, map]);
    return null;
};

const renderComparison = (
    oldValue: string | undefined,
    newValue: string | undefined
) => (
    <Box display="flex" alignItems="center" gap={2} sx={{ padding: 1 }}>
        <Box flex={1}>
            <Typography variant="body2" color="textSecondary">
                {oldValue ? oldValue : "Non défini"}
            </Typography>
        </Box>
        <Box flex={1} sx={{ textAlign: "center" }}>
            <Typography>
                <FontAwesomeIcon icon={faArrowRight} />
            </Typography>
        </Box>
        <Box flex={1}>
            <Typography variant="body2">
                {newValue ? newValue : "Non défini"}
            </Typography>
        </Box>
    </Box>
);

const zoneColors: Record<string, string> = {
    A:    "#F5E0C9",
    Nb:    "#DCEBD3",
    Ne:   "#F7FFE0",
    Nj:   "#BFF0E5",
    NI:   "#C0C0C0",
    NI1:  "#C3E49A",
    Nr:   "#DFF3A1",
    UAv:  "#F8CAC3",
    UBa:  "#DFD8EB",
    UBb:  "#F9CFFD",
    UBb1: "#FBDFFF",
    UBc:  "#DDE8FF",
    UD:   "#7E8BF0",
    UE:   "#C09DA0",
    UXc:  "#A7A0DC",
    // --- Ajouter ci-dessous ---
    UC:   "#FFE8B2",  // un beige orangé léger
    UA:   "#FFD1CF",  // un rose corail doux
    UAa:  "#FFB3A7",  // un saumon pastel un peu plus prononcé
    UB:   "#E8D6F4",  // violet pastel clair
    UY:   "#FFF7B4",  // jaune doux
    UX:   "#C2D4F2", // bleu pastel doux
    Na:   "#E3F2C1", // vert clair légèrement tonique
    Nc:   "#BFE5D8",
};

const Map: React.FC = () => {
    const [version, setVersion] = useState<"PLU" | "PLUi">("PLU");
    const [searchTerm, setSearchTerm] = useState<string>("");
    const [searchResults, setSearchResults] = useState<NominatimResult[]>([]);
    const [selectedPosition, setSelectedPosition] = useState<LatLng | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [tabIndex, setTabIndex] = useState<number>(0);
    const [selectedStreet, setSelectedStreet] = useState<Street | null>(null);
    const [isLoading2, setIsLoading2] = useState<boolean>(false);
    const [expandedPanel, setExpandedPanel] = useState<number | false>(false);
    const [zones, setZones] = useState<GeoJSON.FeatureCollection | null>(null);

    // Fonction de recherche d'une rue via le service Nominatim
    const searchStreet = async (): Promise<void> => {
        if (searchTerm.length < 3) return;
        setSelectedStreet(null);
        setSelectedPosition(null);
        setSearchResults([]);
        setLoading(true);
        try {
            const response = await axios.get<NominatimResult[]>("https://nominatim.openstreetmap.org/search", {
                params: {
                    q: `${searchTerm}, Bois-le-Roi, France`,
                    format: "json",
                    addressdetails: 1,
                    limit: 5,
                },
            });
            // Filtrer pour ne garder que les résultats correspondant à Bois-le-Roi
            const filteredResults = response.data.filter(
                (result) =>
                    result.address?.village === "Bois-le-Roi" && result.address?.postcode === "77590"
            );
            const uniqueResults = filteredResults.filter(
                (result, index, self) =>
                    index === self.findIndex((r) => r.name === result.name)
            );

            console.log(uniqueResults);
            setSearchResults(uniqueResults.slice(0, 3));

        } catch (error) {
            console.error("Erreur lors de la recherche de rue :", error);
        } finally {
            setLoading(false);
        }
    };

    const handleSelectStreet = async (streetName: string | undefined, lat: string, lon: string): Promise<void> => {
        setSelectedStreet(null);
        setSelectedPosition([parseFloat(lat), parseFloat(lon)]);
        if (!streetName) return;
        setIsLoading2(true);
        try {
            // Récupérer les informations complètes de la rue
            const streetData = await getStreetByName(streetName);
            setSelectedStreet(streetData);
        } catch (error) {
            console.error("Erreur lors de la récupération de la rue :", error);
        } finally {
            // Fin du chargement
            setIsLoading2(false);
        }
        // On vide les résultats de recherche pour masquer la liste
        setSearchResults([]);
    };

    const handleChange = (panel: number) => (
        event: React.SyntheticEvent,
        isExpanded: boolean
    ) => {
        setExpandedPanel(isExpanded ? panel : false);
    };

    useEffect(() => {
        fetch("/data/zone_urba_wgs84.geojson")
            .then(r => r.json())
            .then((gj: GeoJSON.FeatureCollection) => setZones(gj))
            .catch(console.error);
    }, []);

    const styleZone = (feature: any) => {
        const code = feature.properties.LIBELLE as string;
        return {
            fillColor: zoneColors[code] || "#888",  // couleur par défaut si code inconnu
            color:    "#444",  // contour
            weight:    1,
            fillOpacity: 0.6,
        };
    };

    // tooltip permanent au centre (optionnel)
    const onEach = (feature: any, layer: any) => {
        const code = feature.properties.LIBELLE as string;
        layer.bindTooltip(code, {
            permanent: true,
            direction: "center",
            className: "zone-label"
        });
    };

    return (
        <>
            <Box sx={{ p: 2 }}>
                <SectionTitle title={"PLUi de Bois-le-roi"} />
            </Box>
            <Box sx={{ p: 2 }}>
                <Typography variant={"body1"}>
                    Bienvenue sur notre carte interactive dédiée aux changements issus du PLUi de Bois-le-roi. Le PLUi (Plan Local d'Urbanisme intercommunal) est un document stratégique qui définit les règles d'aménagement du territoire et guide le développement urbain. Dans le cadre du PLUi de Bois-le-roi, qui dépend de la ville de Fontainebleau, cette démarche vise à informer les citoyens sur les évolutions et les modifications apportées aux règles d'urbanisme de leur secteur.
                </Typography>
                <Typography variant={"body1"}>
                    Pour explorer ces changements via la carte interactive, suivez ces étapes simples :
                </Typography>
                <Typography variant={"body1"}>
                    <ul>
                        <li><b>Saisissez le nom de votre rue</b> : Entrez uniquement le nom de la rue dans le champ de recherche (il n'est pas nécessaire de préciser le numéro).</li>
                        <li><b>Lancez la recherche</b> : Cliquez sur l'icône « Rechercher » pour obtenir une vue détaillée des modifications et des projets liés à votre environnement.</li>
                    </ul>
                </Typography>
                <Typography variant={"body1"}>
                    Grâce à cette interface intuitive, vous pourrez accéder rapidement à l'information nécessaire et mieux comprendre l'impact du PLUi sur votre cadre de vie.
                </Typography>
            </Box>
            <Box sx={{ p: 2, mb: 6 }}>
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 6, lg: 7 }}>
                        {/* Carte Leaflet */}
                        <MapContainer center={center} zoom={14} style={{ height: "600px", width: "100%"}}>
                            <TileLayer
                                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a> contributors'
                            />

                            {/* Affichage des points d'intérêt */}
                            {points.map((point) => (
                                <Marker key={point.id} position={point.coords} icon={customIcon}>
                                    <Popup>{point.name}</Popup>
                                </Marker>
                            ))}

                            {zones &&
                                <GeoJSON
                                    data={zones}
                                    style={styleZone}
                                    onEachFeature={onEach}
                                />
                            }

                            {/* Zoom et marqueur pour la rue sélectionnée */}
                            <FlyToLocation position={selectedPosition} />
                            {selectedPosition && (
                                <Marker position={selectedPosition} icon={customIcon}>
                                    <Popup>Rue sélectionnée</Popup>
                                </Marker>
                            )}
                        </MapContainer>
                    </Grid>

                    <Grid size={{ xs: 12, md: 6, lg: 5 }}>
                        {/* Onglets Material UI */}
                        <Tabs
                            value={tabIndex}
                            onChange={(event, newIndex) => setTabIndex(newIndex)}
                            indicatorColor="primary"
                            textColor="primary"
                            variant="fullWidth"
                        >
                            <Tab label="Légende" />
                            <Tab label="Rechercher sa rue" />
                        </Tabs>

                        {tabIndex === 0 && (
                            <Box sx={{ mt: 2 }}>
                                <Typography variant="subtitle1" gutterBottom>
                                    Sélectionnez le PLU à afficher :
                                </Typography>
                                <RadioGroup
                                    value={version}
                                    onChange={(e) => setVersion(e.target.value as "PLU" | "PLUi")}
                                >
                                    <FormControlLabel
                                        value="PLU"
                                        control={<Radio />}
                                        label="PLU de Bois-le-roi - 2023"
                                    />
                                    <FormControlLabel
                                        value="PLUi"
                                        control={<Radio disabled={true} />}
                                        label="PLUi - 2025 (en cours d'intégration)"
                                    />
                                </RadioGroup>
                            </Box>
                        )}

                        {tabIndex === 1 && (
                            <div style={{ marginTop: 20 }}>
                                <Grid container spacing={2}>
                                    <Grid size={{ xs: 9 }}>
                                        <TextField
                                            label="Rechercher une rue"
                                            variant="outlined"
                                            fullWidth
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            disabled={loading}
                                            placeholder="Rechercher une rue à Bois-le-Roi..."
                                        />
                                    </Grid>
                                    <Grid size={{ xs: 3 }}>
                                        <Button
                                            variant="contained"
                                            onClick={searchStreet}
                                            disabled={loading}
                                            fullWidth
                                            sx={{ height: "100%" }}
                                        >
                                            {loading ? "Recherche..." : <FontAwesomeIcon icon={faSearch} />}
                                        </Button>
                                    </Grid>
                                </Grid>

                                {searchResults.length > 0 && (
                                    <>
                                        <Typography variant="body2" color="textSecondary" sx={{ pt: 2 }}>
                                            Résultat(s) de votre recherche :
                                        </Typography>
                                        {searchResults.map((result, index) => (
                                            <Card key={index} sx={{ mt: 2, mb: 2 }}>
                                                <CardContent>
                                                    <Grid container spacing={2} alignItems="center">
                                                        <Grid size={{ xs: 8 }}>
                                                            <Typography variant="body1">
                                                                {result.address.road}
                                                            </Typography>
                                                            <Typography variant="body2" color="textSecondary">
                                                                {result.address.village} {result.address.postcode}, {result.address.country}
                                                            </Typography>
                                                        </Grid>
                                                        <Grid size={{ xs: 4 }}>
                                                            <Button
                                                                variant="contained"
                                                                onClick={() => handleSelectStreet(result.address.road, result.lat, result.lon)}
                                                                fullWidth
                                                            >
                                                                Voir
                                                            </Button>
                                                        </Grid>
                                                    </Grid>
                                                </CardContent>
                                            </Card>
                                        ))}
                                    </>
                                )}
                                {isLoading2 && <CircularProgress />}

                                {selectedStreet && (
                                    <div style={{ marginTop: "1rem" }}>
                                        <Typography variant="body2" gutterBottom>
                                            Sélection <FontAwesomeIcon icon={faArrowRight} style={{ marginLeft: "10px"}}/> <Chip label={selectedStreet.name} sx={{ boxShadow: 1, marginLeft: '10px' }} />
                                        </Typography>
                                        {selectedStreet.sections && selectedStreet.sections.length > 0 ? (
                                            selectedStreet.sections.length === 1 ? (
                                                // Affichage direct si une seule section
                                                <Card sx={{ mt: 2 }}>
                                                    <CardContent>
                                                        <Typography variant="subtitle2" sx={{ pb: 3 }}>
                                                            {selectedStreet.sections[0].comment}
                                                        </Typography>
                                                        <Grid container spacing={4} sx={{ pb: 3 }}>
                                                            <Grid size={{ xs: 6 }} sx={{ display: "flex", justifyContent: "center" }}>
                                                                <Chip label={"Valeurs actuelles"} />
                                                            </Grid>
                                                            <Grid size={{ xs: 6 }} sx={{ display: "flex", justifyContent: "center" }}>
                                                                <Chip label={"Nouvelles valeurs"} color={"primary"} />
                                                            </Grid>
                                                        </Grid>
                                                        <Typography variant="subtitle2" color="textSecondary">
                                                            Classification PLUi
                                                        </Typography>
                                                        {renderComparison(selectedStreet.sections[0].oldClassificationName, selectedStreet.sections[0].newClassificationName)}

                                                        <Divider sx={{ marginY: 2 }} />

                                                        <Typography variant="subtitle2" color="textSecondary">
                                                            Emprise au sol maximum des constructions
                                                        </Typography>
                                                        {renderComparison(selectedStreet.sections[0].oldMaxBuildingFootprint, selectedStreet.sections[0].newMaxBuildingFootprint)}

                                                        <Divider sx={{ marginY: 2 }} />

                                                        <Typography variant="subtitle2" color="textSecondary">
                                                            Recul d'implantation
                                                        </Typography>
                                                        {renderComparison(selectedStreet.sections[0].oldImplantationRetreat, selectedStreet.sections[0].newImplantationRetreat)}

                                                        <Divider sx={{ marginY: 2 }} />

                                                        <Typography variant="subtitle2" color="textSecondary">
                                                            Implantation
                                                        </Typography>
                                                        {renderComparison(selectedStreet.sections[0].oldBoundaryImplantation, selectedStreet.sections[0].newBoundaryImplantation)}

                                                        <Divider sx={{ marginY: 2 }} />

                                                        <Typography variant="subtitle2" color="textSecondary">
                                                            Bande de recul
                                                        </Typography>
                                                        {renderComparison(selectedStreet.sections[0].oldRetreatBand, selectedStreet.sections[0].newRetreatBand)}
                                                    </CardContent>
                                                </Card>
                                            ) : (
                                                // Affichage dans un accordéon si plusieurs sections
                                                <>
                                                    {selectedStreet.sections.map((section) => (
                                                        <Accordion
                                                            key={section.id}
                                                            expanded={expandedPanel === section.id}
                                                            onChange={handleChange(section.id)}
                                                        >
                                                            <AccordionSummary expandIcon={<FontAwesomeIcon icon={faChevronDown} /> }>
                                                                <Typography variant="subtitle1">
                                                                    { section.description }
                                                                </Typography>
                                                            </AccordionSummary>
                                                            <AccordionDetails>
                                                                <Typography variant="subtitle2" sx={{ pb: 2 }}>
                                                                    {section.comment}
                                                                </Typography>
                                                                <Grid container spacing={4} sx={{ pb: 3 }}>
                                                                    <Grid size={{ xs: 6 }} sx={{ display: "flex", justifyContent: "center" }}>
                                                                        <Chip label={"Valeurs actuelles"} />
                                                                    </Grid>
                                                                    <Grid size={{ xs: 6 }} sx={{ display: "flex", justifyContent: "center" }}>
                                                                        <Chip label={"Nouvelles valeurs"} color={"primary"} />
                                                                    </Grid>
                                                                </Grid>
                                                                <Typography variant="subtitle2" color="textSecondary">
                                                                    Classification PLUi
                                                                </Typography>
                                                                {renderComparison(section.oldClassificationName, section.newClassificationName)}

                                                                <Divider sx={{ marginY: 2 }} />

                                                                <Typography variant="subtitle2" color="textSecondary">
                                                                    Emprise au sol maximum des constructions
                                                                </Typography>
                                                                {renderComparison(section.oldMaxBuildingFootprint, section.newMaxBuildingFootprint)}

                                                                <Divider sx={{ marginY: 2 }} />

                                                                <Typography variant="subtitle2" color="textSecondary">
                                                                    Recul d'implantation
                                                                </Typography>
                                                                {renderComparison(section.oldImplantationRetreat, section.newImplantationRetreat)}

                                                                <Divider sx={{ marginY: 2 }} />

                                                                <Typography variant="subtitle2" color="textSecondary">
                                                                    Implantation
                                                                </Typography>
                                                                {renderComparison(section.oldBoundaryImplantation, section.newBoundaryImplantation)}

                                                                <Divider sx={{ marginY: 2 }} />

                                                                <Typography variant="subtitle2" color="textSecondary">
                                                                    Bande de recul
                                                                </Typography>
                                                                {renderComparison(section.oldRetreatBand, section.newRetreatBand)}
                                                            </AccordionDetails>
                                                        </Accordion>
                                                    ))}
                                                </>
                                            )
                                        ) : (
                                            <Typography variant="body2" color="textSecondary">
                                                Aucune section renseignée pour cette rue.
                                            </Typography>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </Grid>
                </Grid>
            </Box>
        </>
    );
};

export default Map;